import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { BrandWordmark } from "../components/BrandArtwork";
import { auth0LoginParams } from "./auth0Config";

const proofThreads = [
  {
    label: "A week that changed",
    kicker: "Travel without panic",
    messages: [
      ["runner", "Travel blew up today's workout. Can we move forward without cramming the missed miles into the weekend?"],
      ["coach", "Yes. I marked today skipped. Tomorrow is a short flat shakeout, easy effort only. No extras. The goal is to loosen up for Sunday, not chase missed volume."],
    ],
  },
  {
    label: "A signal worth remembering",
    kicker: "Learn from the good days",
    messages: [
      ["runner", "Logged the easy five. It was warm, but the calf felt quiet the whole run."],
      ["coach", "That is a useful green light. We will move forward conservatively and keep the next run easy so you absorb it. Progress, not make-up mileage."],
    ],
  },
  {
    label: "A workout with judgment",
    kicker: "Progress without proving",
    messages: [
      ["runner", "What pace should I target for today's 5K-effort repeats?"],
      ["coach", "Keep it current-fitness based, not goal pace. Start controlled, settle in only if it feels smooth, and finish feeling like you could do one more rep."],
    ],
  },
];

const learningSignals = [
  ["Observe", "Workout feedback, recovery, soreness, consistency, missed days, and the sessions that click."],
  ["Interpret", "Chara looks for patterns in how your body responds instead of treating every week like a blank slate."],
  ["Progress", "Your training evolves with better judgment: when to add stress, hold steady, pull back, or change the stimulus."],
];

const faqs = [
  ["Is Chara just a training-plan generator?", "No. Chara starts with a structured training block, then coaches the decisions that make the block useful: workout feedback, missed days, progress signals, recovery, and the next smart adjustment."],
  ["How does Chara learn what training works for me?", "Chara uses the context you share during onboarding and the feedback you give as training unfolds. Completed workouts, how sessions felt, recurring soreness, strong days, missed runs, and recovery patterns all help shape future decisions."],
  ["Does Chara only adapt when something goes wrong?", "No. Handling travel, fatigue, and soreness matters, but that is the baseline. Chara is also designed to learn from the workouts that go well and progress your training when the evidence supports it."],
  ["How do I talk to my coach?", "Chara is text-first. During the beta, coaching is delivered through Telegram, with the system designed around quick conversations when you need a decision without opening another dashboard."],
  ["Will Chara tell me to rest?", "Yes. A trustworthy coach does not turn every question into more mileage. Chara is designed to reduce, adjust, or stop training when the context calls for caution."],
  ["What kinds of goals can Chara coach?", "Chara supports runners building consistency as well as athletes training for races from the 5K through the marathon and ultra distances. Your goal sets the direction; your training feedback shapes the path."],
];

function ConfigMissingScreen() {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1>Auth0 config missing</h1>
        <p>Set `REACT_APP_AUTH0_DOMAIN`, `REACT_APP_AUTH0_CLIENT_ID`, and `REACT_APP_AUTH0_AUDIENCE` before enabling login.</p>
      </div>
    </div>
  );
}

function ThreadCard({ thread, featured = false }) {
  return (
    <article className={`proof-thread ${featured ? "featured" : ""}`}>
      <div className="proof-thread-head">
        <div>
          <div className="section-kicker">{thread.kicker}</div>
          <strong>{thread.label}</strong>
        </div>
        <span>Text coaching</span>
      </div>
      <div className="proof-thread-messages">
        {thread.messages.map(([speaker, text]) => (
          <div className={`proof-message ${speaker}`} key={text}>
            <span>{speaker === "runner" ? "Runner" : "Chara"}</span>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

function PublicLanding({ logIn }) {
  const handleLogin = logIn || (() => {});

  return (
    <div className="auth-screen">
      <div className="auth-landing">
        <section className="auth-card auth-card--hero">
          <div className="auth-hero-copy">
            <BrandWordmark />
            <h1>The running coach you can text when life changes the plan.</h1>
            <p>
              Chara builds your training, learns how your body responds, and keeps making the next decision with you as your fitness, feedback, and real life evolve.
            </p>
            <div className="auth-actions">
              <a className="auth-button" href="/onboarding">Join the beta</a>
              <button className="auth-logout-button auth-secondary-button" onClick={() => document.getElementById("proof")?.scrollIntoView({ behavior: "smooth" })}>See Chara coach</button>
              <button className="auth-logout-button auth-secondary-button" onClick={handleLogin}>Log in</button>
            </div>
            <div className="auth-trust-line">Not just a plan. A coaching relationship that gets smarter as you train.</div>
          </div>
          <aside className="hero-coach-note">
            <div className="hero-coach-note-top">
              <span>Coach's read</span>
              <i>Live context</i>
            </div>
            <strong>Progress without proving fitness every day.</strong>
            <p>Chara remembers the signals that matter, protects the work you have already banked, and adapts the next step with a reason.</p>
            <div className="hero-signal-row">
              <span>feedback</span>
              <span>memory</span>
              <span>progression</span>
            </div>
          </aside>
        </section>

        <section className="auth-section auth-section--learning">
          <div className="auth-section-copy">
            <div className="section-kicker">The harder problem</div>
            <h2>Adapting around life is the baseline. Learning what actually makes you better is the point.</h2>
            <p>
              Static plans prescribe workouts. Chara pays attention to how the training lands, remembers the pattern, and uses that evidence to shape what comes next.
            </p>
          </div>
          <div className="learning-signal-grid">
            {learningSignals.map(([title, copy], index) => (
              <div className="learning-signal" key={title}>
                <span>0{index + 1}</span>
                <strong>{title}</strong>
                <p>{copy}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="proof" className="auth-section auth-section--proof">
          <div className="auth-section-copy">
            <div className="section-kicker">Coaching, not notifications</div>
            <h2>The useful moments happen in the thread.</h2>
            <p>These anonymized examples are adapted from real Chara coaching conversations.</p>
          </div>
          <div className="proof-thread-grid">
            {proofThreads.map((thread, index) => <ThreadCard key={thread.label} thread={thread} featured={index === 1} />)}
          </div>
        </section>

        <section className="auth-section auth-section--contrast">
          <div className="auth-section-copy">
            <div className="section-kicker">A coach that remembers</div>
            <h2>Training gets more personal the longer your coach knows you.</h2>
            <p>
              Chara learns from injury history, fatigue patterns, missed workouts, breakthrough sessions, and the training choices that have worked before. It uses that context when the next decision is not obvious.
            </p>
          </div>
          <div className="auth-philosophy-points">
            <div>Remembers the context</div>
            <div>Learns from progress</div>
            <div>Adapts with a reason</div>
          </div>
        </section>

        <section className="auth-section auth-section--faq">
          <div className="auth-section-copy">
            <div className="section-kicker">Questions, answered</div>
            <h2>What runners should know before the first text.</h2>
          </div>
          <div className="faq-list">
            {faqs.map(([question, answer]) => (
              <details className="faq-item" key={question}>
                <summary>{question}<span>+</span></summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="auth-card auth-final-cta">
          <div>
            <div className="section-kicker">Run with Joy</div>
            <h2>Train with a coach that keeps learning you.</h2>
            <p>Build the relationship before race day asks for it.</p>
          </div>
          <div className="auth-actions">
            <a className="auth-button" href="/onboarding">Join the beta</a>
            <button className="auth-logout-button auth-secondary-button" onClick={handleLogin}>Log in</button>
          </div>
        </section>
        <footer className="site-legal-links">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms and Conditions</a>
        </footer>
      </div>
    </div>
  );
}

function AuthenticatedGate({ children }) {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const logIn = () => loginWithRedirect(auth0LoginParams());

  if (isLoading) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1>Loading Chara Console</h1>
          <p>Checking your session.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PublicLanding logIn={logIn} />;
  }

  return children;
}

export default function AuthGate({ auth0Configured, children, devPreview = null }) {
  if (devPreview?.isActive) {
    return (
      <>
        <div className="dev-preview-banner">{devPreview.label}</div>
        {children}
      </>
    );
  }
  if (devPreview) {
    return <PublicLanding />;
  }
  if (!auth0Configured) {
    return <ConfigMissingScreen />;
  }
  return <AuthenticatedGate>{children}</AuthenticatedGate>;
}
